/* eslint-disable @typescript-eslint/no-explicit-any */
import { Stack, Typography } from '@mui/material';
import InstaSpinner from 'components/InstaSpinner';
import PlanActivatedPopupBirdFeeder from 'components/PopUps/BirdFeederPlanActivatedPopup';
import FreeTrialActivatedPopup from 'components/PopUps/FreeTrialActivatedPopup/FreeTrialActivatedPopup';
import PaymentFailedPopup from 'components/PopUps/PaymentFailedPopup/PaymentFailedPopup';
import PaymentConfirmedPopup from 'components/PopUps/PaymentSuccessPopup/paymentSucessPopups';
import { PRODUCT_TYPE } from 'configs/constants';
import PopUp from 'coreComponents/PopUp/PopUp';
import { useLoadingStates } from 'hooks/useLoadingStates';
import { AppBridge } from 'infrastructure/appBridge/appBridge';
import { APP_BRIDGE_CALLBACKS } from 'infrastructure/appBridge/appCallBacks';
import analyticsService, {
  AppEvent
} from 'infrastructure/serviceHandlers/Analytics/Analytics';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  addDeviceToSubscription,
  AddDeviceToSubscriptionPayload
} from 'services/api/devicesApi';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchCurrentPlan } from 'store/reducers/currentPlanReducer';
import { fetchSpaceDevices } from 'store/reducers/deviceInSpaceReducers';
import { fetchOwnerSpace } from 'store/reducers/spaceReducers';
import { fetchAllSubscription } from 'store/reducers/subscriptionReducer';
import {
  SUBSCRIPTION_TYPE,
  TSpaceDevicesState,
  TSpaceState,
  TSubscriptionType
} from 'typings';
import { PopupTypes } from 'typings/popUpTypes';
import { getLocalStorageItem } from 'utils/commonFunctions';
import {
  getAllDevicesByProductType,
  getPlanDetails
} from 'utils/subscriptionFunctions';
import { hideAppButton, isRunningInWebView } from 'utils/webviewHelpers';

function PaymentSuccess() {
  const dispatch = useAppDispatch();
  const { search } = useLocation();
  const [isNewSubscriptionLoading, setIsNewSubscriptionLoading] =
    useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pollingCount, setPollingCount] = useState(0);
  const [isPollingStarted, setIsPollingStarted] = useState(false);
  const [purchaseStatus, setPurchaseStatus] = useState<boolean>(false);
  const [purchasedSubscription, setPurchasedSubscription] =
    useState<TSubscriptionType>();

  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.subscription.paymentStatus'
  });
  const [paymentFailed, setPaymentFailed] = useState<boolean>(false);

  const { spaceData, isLoading: isSpaceDataLoading } =
    useAppSelector<TSpaceState>((state) => state.space);

  const { spaceDevices, isLoading: isSpaceDevicesLoading } =
    useAppSelector<TSpaceDevicesState>((state) => state.spaceDevices);

  const { selectedSubscriptionInfo } = useAppSelector(
    (state) => state.subscription
  );

  const params = new URLSearchParams(search);
  const purchaseId = params.get('purchase_id');
  const userId = getLocalStorageItem('userId');

  useEffect(() => {
    if (isRunningInWebView()) {
      hideAppButton();
    }
  }, [isRunningInWebView]);

  useEffect(() => {
    if (spaceData?.id) dispatch(fetchSpaceDevices(spaceData.id));
  }, [spaceData, dispatch]);

  const isLoading = useLoadingStates([
    isSpaceDataLoading,
    isSpaceDevicesLoading
  ]);

  // console.log('Re-rendering PaymentSuccess with paymentFailed:', paymentFailed);

  const findSubscriptionByPurchaseId = (subscriptions: TSubscriptionType[]) => {
    const found = subscriptions.filter((item: any) => item.id === purchaseId);
    return found.length ? found[0] : false;
  };

  const handleDeviceAdditionDuringNormalFlow = (
    deviceArray: string[],
    cameraCountSupportedByPurchasedPlan: number
  ): AddDeviceToSubscriptionPayload => {
    console.log('deviceArray', deviceArray);
    console.log('camera count supported ', cameraCountSupportedByPurchasedPlan);
    if (deviceArray.length <= cameraCountSupportedByPurchasedPlan) {
      return { device_ids: deviceArray };
    }
    return {
      device_ids: deviceArray.slice(0, cameraCountSupportedByPurchasedPlan)
    };
  };

  const triggerAnalyticsEvent = (subscriptionObj: TSubscriptionType) => {
    console.log(
      'Triggering amplitude event',
      subscriptionObj,
      selectedSubscriptionInfo
    );
    if (subscriptionObj.subscription_type === SUBSCRIPTION_TYPE.TRIAL) {
      analyticsService.trackEvent(AppEvent.TRIAL_CONFIRMED, {
        user_id: userId,
        level_cycle: getPlanDetails(
          selectedSubscriptionInfo?.planSelected?.level,
          subscriptionObj?.subscription_cycle
        ),
        ...(analyticsService.getUserPathFromLocalStorage() && {
          user_path: analyticsService.getUserPathFromLocalStorage()
        }),
        ...(purchasedSubscription?.product_id === PRODUCT_TYPE.BIRD && {
          'bird-user': true
        })
      });
    } else if (subscriptionObj.subscription_type === SUBSCRIPTION_TYPE.PAID) {
      analyticsService.trackEvent(AppEvent.SUBSCRIPTION_PURCHASE_SUCCESS, {
        level_cycle: getPlanDetails(
          selectedSubscriptionInfo?.planSelected?.level,
          subscriptionObj?.subscription_cycle
        ),
        user_id: userId,
        ...(analyticsService.getUserPathFromLocalStorage() && {
          user_path: analyticsService.getUserPathFromLocalStorage()
        }),
        ...(purchasedSubscription?.product_id === PRODUCT_TYPE.BIRD && {
          'bird-user': true
        })
      });
    }
  };

  const addCameraToSubscription = async (
    subscriptionObj: TSubscriptionType
  ) => {
    const devicesBasedOnSubscription = getAllDevicesByProductType(
      spaceDevices,
      subscriptionObj.product_id
    );
    const deviceArray = devicesBasedOnSubscription?.map((el) => el?.id);
    console.log('device Array selected space', deviceArray);
    if (deviceArray) {
      if (deviceArray && deviceArray.length === 0) {
        // if there are no devices, we dont need to add any device to subscription
        setPurchaseStatus(true);
        setIsNewSubscriptionLoading(false);
        if (isRunningInWebView()) {
          console.log('calling app for subscription upgrade');
          AppBridge.sendMessageToApp(
            APP_BRIDGE_CALLBACKS.UPDATED_SUBSCRIPTION,
            true
          );
        }
        return;
      }
      console.log('device Array selected space', deviceArray);
      console.log('spaceDevices', spaceDevices);
      const camerasSupportedFeature = subscriptionObj.supported_features.find(
        (feature) => feature.id === 'cameras_supported'
      );
      const cameraCountSupportedByPurchasedPlan =
        camerasSupportedFeature && camerasSupportedFeature?.value
          ? parseInt(camerasSupportedFeature?.value, 10)
          : 0;

      const payload = handleDeviceAdditionDuringNormalFlow(
        deviceArray,
        cameraCountSupportedByPurchasedPlan
      );
      console.log('payload', payload);

      try {
        const res = await addDeviceToSubscription(subscriptionObj.id, payload);
        console.log('addCameraToSubscription SUCCESS:', res, payload);

        dispatch(fetchAllSubscription());
        dispatch(fetchOwnerSpace());

        if (isRunningInWebView()) {
          console.log('calling app for subscription upgrade');
          AppBridge.sendMessageToApp(
            APP_BRIDGE_CALLBACKS.UPDATED_SUBSCRIPTION,
            true
          );
        }
      } catch (err) {
        console.log('addCameraToSubscription ERROR:', err);
      } finally {
        setPurchaseStatus(true);
        setIsNewSubscriptionLoading(false);
      }
    }
  };

  const polling = () => {
    console.log('Polling function executed');

    setTimeout(() => {
      setPollingCount((prevCount) => {
        const nextCount = prevCount + 1;

        if (nextCount >= 10) {
          console.log(
            'Polling attempts exhausted. Setting paymentFailed to true.'
          );
          setPurchaseStatus(false);
          setPaymentFailed(true);
          setIsNewSubscriptionLoading(false);

          analyticsService.trackEvent(
            selectedSubscriptionInfo.isFreeTrial
              ? AppEvent.TRIAL_PURCHASE_FAILED
              : AppEvent.SUBSCRIPTION_PURCHASE_FAILURE,
            {
              user_id: userId,
              purchase_id: purchaseId,
              reason: 'polling_timeout',
              ...(analyticsService.getUserPathFromLocalStorage() && {
                user_path: analyticsService.getUserPathFromLocalStorage()
              }),
              ...(purchasedSubscription?.product_id === PRODUCT_TYPE.BIRD && {
                'bird-user': true
              })
            }
          );

          return nextCount;
        }

        dispatch(fetchAllSubscription())
          .unwrap()
          .then((res) => {
            const found = findSubscriptionByPurchaseId(res);
            console.log('Found subscription:', found);

            if (found && found.id === purchaseId) {
              try {
                setPurchasedSubscription(found);
                // setProductId(found.product_id);
                addCameraToSubscription(found);
                dispatch(fetchCurrentPlan(found.sku_id));
                triggerAnalyticsEvent(found);
              } catch (error) {
                console.error('Analytics event failed:', error);
                setTimeout(() => triggerAnalyticsEvent(found), 1000);
              }
            } else {
              console.log('pollingCount', nextCount, Date.now());
              polling();
            }
          })
          .catch((err: unknown) => {
            console.log('fetchAllSubscription ERROR:', err);
            polling();
          });

        return nextCount;
      });
    }, 2000);
  };

  useEffect(() => {
    if (spaceDevices && !isSpaceDevicesLoading && !isPollingStarted) {
      setIsNewSubscriptionLoading(true);
      setIsPollingStarted(true);
      polling();
    }
  }, [spaceDevices, isSpaceDevicesLoading]);

  if (isNewSubscriptionLoading || isLoading)
    return (
      <PopUp isOpen type={PopupTypes.TYPE2}>
        <Stack justifyContent="center" alignItems="center">
          <InstaSpinner />
          <Typography variant="body1" mt={4} textAlign="center">
            {t('pleaseWait')}...
          </Typography>
        </Stack>
      </PopUp>
    );

  return (
    <div>
      {purchaseStatus &&
        purchasedSubscription?.subscription_type !== SUBSCRIPTION_TYPE.TRIAL &&
        purchasedSubscription?.product_id === PRODUCT_TYPE.HOME_SECURITY && (
          <PaymentConfirmedPopup
            planCycle={purchasedSubscription?.subscription_cycle}
            planName={purchasedSubscription?.sku_name}
          />
        )}
      {purchaseStatus &&
        purchasedSubscription?.subscription_type === SUBSCRIPTION_TYPE.TRIAL &&
        purchasedSubscription?.product_id === PRODUCT_TYPE.HOME_SECURITY && (
          <FreeTrialActivatedPopup isUpgrade={false} />
        )}

      {purchaseStatus &&
        purchasedSubscription?.product_id === PRODUCT_TYPE.BIRD && (
          <PlanActivatedPopupBirdFeeder
            isUpgrade={false}
            isFreeTrial={
              purchasedSubscription?.subscription_type ===
              SUBSCRIPTION_TYPE.TRIAL
            }
          />
        )}

      {paymentFailed && <PaymentFailedPopup />}
    </div>
  );
}

export default PaymentSuccess;
